import React, { useEffect, useState } from "react";
import { FaTimes, FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaCity, FaBuilding, FaIdCard, FaStickyNote } from "react-icons/fa";
import { toast } from "react-toastify";
import { addClient, updateClient } from "../services/ClientService";

const ClientForum = ({ isOpen, onClose, onClientSaved, client }) => {
    const [formData, setFormData] = useState({
        nom_affichage: "",
        nom_contact: "",
        email: "",
        telephone: "",
        adresse_ligne1: "",
        adresse_ligne2: "",
        code_postal: "",
        ville: "",
        code_pays: "FR",
        siret: "",
        numero_tva: "",
        notes: "",
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const requiredFields = ["nom_affichage"];

    // Remplir le formulaire si on modifie un client
    useEffect(() => {
        if (client) {
            setFormData({
                nom_affichage: client.nom_affichage || "",
                nom_contact: client.nom_contact || "",
                email: client.email || "",
                telephone: client.telephone || "",
                adresse_ligne1: client.adresse_ligne1 || "",
                adresse_ligne2: client.adresse_ligne2 || "",
                code_postal: client.code_postal || "",
                ville: client.ville || "",
                code_pays: client.code_pays || "FR",
                siret: client.siret || "",
                numero_tva: client.numero_tva || "",
                notes: client.notes || "",
                id: client.id
            });
        }
    }, [client]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setErrors({ ...errors, [e.target.name]: "" });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = {};
        requiredFields.forEach(f => {
            if (!formData[f]) newErrors[f] = "Ce champ est obligatoire";
        });

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            toast.error("Veuillez remplir les champs obligatoires");
            return;
        }

        setLoading(true);
        try {
            let savedClient;
            if (client) {
                savedClient = await updateClient(formData);
                toast.success("Client mis à jour avec succès !");
            } else {
                savedClient = await addClient(formData);
                toast.success("Client ajouté avec succès !");
            }

            onClientSaved(savedClient);
            onClose();

            // Réinitialiser le formulaire après succès
            setFormData({
                nom_affichage: "",
                nom_contact: "",
                email: "",
                telephone: "",
                adresse_ligne1: "",
                adresse_ligne2: "",
                code_postal: "",
                ville: "",
                code_pays: "FR",
                siret: "",
                numero_tva: "",
                notes: "",
            });
            setErrors({});
        } catch (err) {
            toast.error(err.message || "Erreur lors de l'enregistrement du client");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-start pt-20 z-50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-6 relative">
                <button
                    className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
                    onClick={onClose}
                >
                    <FaTimes />
                </button>
                <h2 className="text-lg font-bold mb-4">{client ? "Modifier le client" : "Ajouter un client"}</h2>

                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
                    {/* Raison sociale */}
                    <div className="col-span-2">
                        <label className="block text-sm font-medium mb-1">
                            Raison sociale <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <input
                                name="nom_affichage"
                                value={formData.nom_affichage}
                                onChange={handleChange}
                                className={`w-full px-3 py-2 border rounded-md pr-10 ${errors.nom_affichage ? "border-red-500" : "border-gray-300"}`}
                                placeholder="Nom de l'entreprise"
                            />
                            <FaUser className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        </div>
                        {errors.nom_affichage && <p className="text-red-500 text-xs mt-1">{errors.nom_affichage}</p>}
                    </div>

                    {/* Nom contact */}
                    <div>
                        <label className="block text-sm font-medium mb-1">Nom contact</label>
                        <div className="relative">
                            <input
                                name="nom_contact"
                                value={formData.nom_contact}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10"
                                placeholder="Nom du contact"
                            />
                            <FaUser className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        </div>
                    </div>

                    {/* Email */}
                    <div>
                        <label className="block text-sm font-medium mb-1">Email</label>
                        <div className="relative">
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10"
                                placeholder="exemple@mail.com"
                            />
                            <FaEnvelope className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        </div>
                    </div>

                    {/* Téléphone */}
                    <div>
                        <label className="block text-sm font-medium mb-1">Téléphone</label>
                        <div className="relative">
                            <input
                                name="telephone"
                                value={formData.telephone}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10"
                                placeholder="+33 6 12 34 56 78"
                            />
                            <FaPhone className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        </div>
                    </div>

                    {/* Adresse ligne 1 */}
                    <div className="col-span-2">
                        <label className="block text-sm font-medium mb-1">Adresse ligne 1</label>
                        <div className="relative">
                            <input
                                name="adresse_ligne1"
                                value={formData.adresse_ligne1}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10"
                                placeholder="N° et rue"
                            />
                            <FaMapMarkerAlt className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        </div>
                    </div>

                    {/* Adresse ligne 2 */}
                    <div className="col-span-2">
                        <label className="block text-sm font-medium mb-1">Adresse ligne 2</label>
                        <div className="relative">
                            <input
                                name="adresse_ligne2"
                                value={formData.adresse_ligne2}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10"
                                placeholder="Complément (Bâtiment, étage...)"
                            />
                            <FaMapMarkerAlt className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        </div>
                    </div>

                    {/* Code postal */}
                    <div>
                        <label className="block text-sm font-medium mb-1">Code postal</label>
                        <input
                            name="code_postal"
                            value={formData.code_postal}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="75001"
                        />
                    </div>

                    {/* Ville */}
                    <div>
                        <label className="block text-sm font-medium mb-1">Ville</label>
                        <div className="relative">
                            <input
                                name="ville"
                                value={formData.ville}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10"
                                placeholder="Paris"
                            />
                            <FaCity className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        </div>
                    </div>

                    {/* SIRET */}
                    <div>
                        <label className="block text-sm font-medium mb-1">SIRET</label>
                        <div className="relative">
                            <input
                                name="siret"
                                value={formData.siret}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10"
                                placeholder="12345678901234"
                            />
                            <FaBuilding className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        </div>
                    </div>

                    {/* N° TVA */}
                    <div>
                        <label className="block text-sm font-medium mb-1">N° TVA</label>
                        <div className="relative">
                            <input
                                name="numero_tva"
                                value={formData.numero_tva}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10"
                                placeholder="FR12345678901"
                            />
                            <FaIdCard className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        </div>
                    </div>

                    {/* Notes */}
                    <div className="col-span-2">
                        <label className="block text-sm font-medium mb-1">Notes</label>
                        <div className="relative">
                            <textarea
                                name="notes"
                                value={formData.notes}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10"
                                placeholder="Notes supplémentaires"
                            />
                            <FaStickyNote className="absolute right-3 top-2 text-gray-400" />
                        </div>
                    </div>

                    {/* Submit */}
                    <div className="col-span-2">
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-color3 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
                        >
                            {loading ? (client ? "Mise à jour en cours..." : "Ajout en cours...")
                                : (client ? "Mettre à jour le client" : "Ajouter le client")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ClientForum;
